import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import color from 'tinycolor2';
import {
  Manager,
  Target,
  Popper,
  Arrow,
} from 'react-popper';

import messages from './messages';

const GradesSummaryContainer = styled.div`
  height: 20px;
  width: 100%;
  position: relative;
`;

const GradesSummaryBackground = styled.div`
  height: 10px;
  border-radius: 5px;
  background-color: ${(props) => color(props.theme['color.input.foreground']).setAlpha(0.1).toString()};
  margin: 5px 0;
  position: absolute;
  width: 100%;
  overflow: hidden;
`;

const RangeOfGrades = styled.div`
  height: 10px;
  background-color: ${(props) => props.theme['color.primary']};
  margin-left: ${(props) => props.left};
  width: ${(props) => props.width};
`;

const MeanMarker = styled.div`
  height: 20px;
  width: 1px;
  background-color: ${(props) => color(props.theme['color.input.foreground']).setAlpha(0.8).toString()};
  position: absolute;
  left: ${(props) => props.left};
`;

const DialogOuter = styled.div`
  background-color: ${(props) => props.theme['color.surface']};
  display: flex;
  flex-direction: column;
  min-width: 132px;

  @media (max-width: 480px) {
    padding: 15px;
    height: 100%;
    width: 100%;
    border-radius: 4px;
    box-shadow: ${(props) => props.theme['shadow.modal']};
    box-sizing: border-box !important;
  }

  @media (min-width: 480px) {
    ${(props) => props.marginBottom && 'margin-bottom: 14px;'}
    border-radius: 4px;
    box-shadow: ${(props) => props.theme['shadow.modal']};
    padding: 16px;
    box-sizing: border-box;
  }
`;

const DialogBody = styled.div`
  color: ${(props) => props.theme['color.text.body']};
  font-size: 14px;
  line-height: 18px;
  flex-grow: 1;
  display: flex;
`;

const DialogArrow = styled(Arrow)`
  content: "";
  position: absolute;
  bottom: 2px;
  left: 50%;
  border-style: solid;
  border-width: 12px 12px 0 12px;
  border-color: ${(props) => props.theme['color.surface']} transparent transparent transparent;

  @media (max-width: 480px) {
    display: none;
  }
`;

const DialogScoreSummary = styled.div`
  display: grid;
  grid-template-areas: "lowest val-lowest"
    "mean val-mean"
    "median val-median"
    "highest val-highest";
  column-gap: 10px;
  row-gap: 14px;
  grid-template-rows: repeat(1fr, 4);
  grid-template-columns: repeat(1fr, 2);
  text-align: left;
`;

const DialogScoreSummaryLabel = styled.div`
  grid-area: ${(props) => props.gridArea};
  font-weight: bold;
`;

const DialogScoreSummaryValue = styled.div`
  grid-area: ${(props) => props.gridArea};
`;

function GradesSummary({ lowest, mean, median, highest, totalScore, popperRef, ...rest }) {
  const [isHovered, setIsHovered] = useState(false);
  const onMouseEnter = useCallback(() => setIsHovered(true), [setIsHovered]);
  const onMouseLeave = useCallback(() => setIsHovered(false), [setIsHovered]);

  const rangeOfGradesLeft = `${(lowest / totalScore) * 100}%`;
  const rangeOfGradesWidth = `${((highest - lowest) / totalScore) * 100}%`;
  const meanMarkerLeft = `${(mean / totalScore) * 100}%`;

  return (
    <Manager>
      <Target
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <GradesSummaryContainer
          {...rest}
        >
          <GradesSummaryBackground>
            <RangeOfGrades
              left={rangeOfGradesLeft}
              width={rangeOfGradesWidth}
            />
          </GradesSummaryBackground>
          <MeanMarker
            left={meanMarkerLeft}
          />
        </GradesSummaryContainer>
      </Target>
      {isHovered &&
        <GradesSummaryPopper lowest={lowest} mean={mean} median={median} highest={highest} popperRef={popperRef} />
      }
    </Manager>
  );
}

GradesSummary.propTypes = {
  lowest: PropTypes.number.isRequired,
  mean: PropTypes.number.isRequired,
  median: PropTypes.number.isRequired,
  highest: PropTypes.number.isRequired,
  totalScore: PropTypes.number.isRequired,
  popperRef: PropTypes.object.isRequired,
};

function GradesSummaryPopper({ lowest, mean, median, highest, popperRef }) {
  return ReactDOM.createPortal(
    <Popper
      placement="top"
      modifiers={{
        preventOverflow: { escapeWithReference: true },
      }}
    >
      <DialogOuter marginBottom>
        <DialogBody>
          <DialogScoreSummary>
            <DialogScoreSummaryLabel gridArea="lowest"><FormattedMessage {...messages.lowest} /></DialogScoreSummaryLabel>
            <DialogScoreSummaryValue gridArea="val-lowest">{lowest}pts</DialogScoreSummaryValue>
            <DialogScoreSummaryLabel gridArea="mean"><FormattedMessage {...messages.mean} /></DialogScoreSummaryLabel>
            <DialogScoreSummaryValue gridArea="val-mean">{mean}pts</DialogScoreSummaryValue>
            <DialogScoreSummaryLabel gridArea="median"><FormattedMessage {...messages.median} /></DialogScoreSummaryLabel>
            <DialogScoreSummaryValue gridArea="val-median">{median}pts</DialogScoreSummaryValue>
            <DialogScoreSummaryLabel gridArea="highest"><FormattedMessage {...messages.highest} /></DialogScoreSummaryLabel>
            <DialogScoreSummaryValue gridArea="val-highest">{highest}pts</DialogScoreSummaryValue>
          </DialogScoreSummary>
        </DialogBody>
      </DialogOuter>
      <DialogArrow />
    </Popper>,
    popperRef.current,
  );
}

function GradesSummaryPlaceholder(props) {
  return (
    <GradesSummaryContainer {...props}>
      <GradesSummaryBackground />
    </GradesSummaryContainer>
  );
}

export default React.memo(GradesSummary);
export { GradesSummaryPlaceholder };
